// SCREENS-VIEWER: the selected session, filling the main area — a slim toolbar over the live
// screen. Watch / Take over switch the connection's role; Hand back (with a note) gives an agent its
// screen back; End (the account's owner or an admin) or Done (my own login browser) closes it;
// Full screen; Open in new window (`/screens/<lease>?c=<slug>`, the same pane alone).
import { Badge, cn, InfoTip, ToolbarButton, toast, WorkerBadge, workerName } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckIcon,
  ExternalLinkIcon,
  EyeIcon,
  HandIcon,
  MaximizeIcon,
  SquareIcon,
  TimerIcon,
  Undo2Icon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { useSession } from "../use-feature";
import { AddressBar } from "./address-bar";
import { AskCountdown } from "./ask-countdown";
import { HandBackDialog, leaseWhat } from "./hand-back-dialog";
import { LiveScreen } from "./live-screen";
import {
  drivenBy,
  findLease,
  type Lease,
  screensKeys,
  screensOverviewQuery,
} from "./screens-queries";
import { releaseOwnLease } from "./session-actions";

const fail = (e: unknown, what: string) =>
  toast.add({ title: problemText(e, what), type: "error" });

export function standaloneHref(slug: string, leaseId: string): string {
  return `/screens/${leaseId}?c=${encodeURIComponent(slug)}`;
}

export function SessionPane({
  api,
  co,
  slug,
  leaseId,
  lease,
  me,
  starting,
  standalone = false,
  onClosed,
}: {
  api: Api;
  co: string;
  slug: string;
  leaseId: string;
  /** Null while it is being opened, or once it is gone from the overview. */
  lease: Lease | null;
  me: string | null;
  starting?: string | null;
  standalone?: boolean;
  /** I ended it (End / Done). */
  onClosed?: () => void;
}) {
  const qc = useQueryClient();
  const pane = useRef<HTMLDivElement>(null);
  const [watchOnly, setWatchOnly] = useState(false);
  const [handBack, setHandBack] = useState(false);
  // Another session: start from what I may do there.
  const [shownLease, setShownLease] = useState(leaseId);
  if (shownLease !== leaseId) {
    setShownLease(leaseId);
    setWatchOnly(false);
    setHandBack(false);
  }
  // The overview and every needs-you card read of this company (a card shows the hand-back).
  const refresh = () => qc.invalidateQueries({ queryKey: screensKeys.all(co) });

  const driving = !!lease && drivenBy(lease, me);
  const role = driving && !watchOnly ? "control" : "watch";
  const mine = !!lease && lease.holder.kind === "person" && lease.holder.membership_id === me;
  const otherDriver =
    lease?.controller && lease.controller.membership_id !== me ? lease.controller : null;

  // My own login browser (Accounts › Log in) closes with Done; anything else ends with End.
  const myLogin = mine && !!lease?.account;
  // Pane-local effects of a call apply only while its session is still the one shown.
  const shown = useRef<string | null>(leaseId);
  shown.current = leaseId;
  // Unmounted: nothing is shown, so late answers change nothing here.
  useEffect(
    () => () => {
      shown.current = null;
    },
    [],
  );
  const handBackFor = leaseId;

  const takeOver = useMutation({
    mutationFn: (id: string) => api.client.screens.takeOver({ params: { co, id } }),
    onSuccess: (l, id) => {
      qc.setQueryData(screensKeys.overview(co), (o: unknown) => patchLease(o, l));
      if (shown.current === id) setWatchOnly(false);
      void refresh();
    },
    onError: (e) => fail(e, "Could not take it over."),
  });
  const end = useMutation({
    mutationFn: (v: { id: string; login: boolean }) =>
      v.login
        ? releaseOwnLease(api, co, v.id)
        : api.client.screens.stop({ params: { co, id: v.id } }),
    onSuccess: (_l, v) => {
      void refresh();
      if (shown.current === v.id) onClosed?.();
    },
    onError: (e, v) => fail(e, v.login ? "Could not close it." : "Could not end it."),
  });
  // PERSISTENT-SCREENS: more time for the person (a slow 2FA code).
  const extend = useMutation({
    mutationFn: (askId: string) =>
      api.client.screens.askExtend({ params: { co, id: askId }, body: {} }),
    onSuccess: () => void refresh(),
    onError: (e) => fail(e, "Could not extend."),
  });

  const live = !!lease && lease.state !== "queued";
  const fullScreen = () => {
    const el = pane.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void el.requestFullscreen?.().catch(() => {});
  };

  return (
    <div
      ref={pane}
      data-slot="session-pane"
      data-role={role}
      className="flex min-h-0 min-w-0 flex-1 flex-col bg-background"
    >
      <div
        data-slot="session-toolbar"
        className="flex h-11 shrink-0 items-center gap-2 overflow-hidden border-b border-border px-3"
      >
        {lease ? (
          <>
            <WorkerBadge kind={lease.holder.kind} size="sm" />
            <span className="min-w-0 shrink truncate text-sm font-medium">
              {workerName(lease.holder.name)}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground max-sm:hidden">
              {[lease.task?.title, leaseWhat(lease)].filter(Boolean).join(" · ")}
            </span>
            <span
              data-slot="role-note"
              className={cn(
                "min-w-0 shrink truncate text-xs",
                role === "control" ? "font-medium text-warning-text" : "text-muted-foreground",
              )}
            >
              {role === "control"
                ? mine
                  ? "You're driving"
                  : "Driving · agent paused"
                : otherDriver
                  ? `${otherDriver.name} is driving`
                  : "Watching"}
            </span>
            {lease.needs_you?.expires_at ? (
              // PERSISTENT-SCREENS: the screen stays open for the person until this runs out.
              <span data-slot="needs-you-timer" className="inline-flex shrink-0 items-center gap-1">
                <AskCountdown expiresAt={lease.needs_you.expires_at} />
                {lease.can.control && lease.needs_you.ask_id ? (
                  <ToolbarButton
                    icon={TimerIcon}
                    label="Extend"
                    aria-label="Extend 15 min"
                    disabled={extend.isPending}
                    onClick={() => extend.mutate(lease.needs_you?.ask_id as string)}
                  />
                ) : null}
              </span>
            ) : null}
            {lease.operator_control || lease.operator_viewing > 0 ? (
              <span
                data-slot="operator-presence"
                className="inline-flex min-w-0 shrink items-center gap-1 overflow-hidden text-xs whitespace-nowrap text-muted-foreground max-lg:hidden"
              >
                {lease.operator_control ? (
                  <Badge variant="warning" data-slot="operator-control">
                    Operator in control
                  </Badge>
                ) : null}
                {lease.operator_viewing > 0 ? (
                  <span data-slot="operator-viewing">
                    {lease.operator_viewing} operator watching
                  </span>
                ) : null}
              </span>
            ) : null}
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
            {starting ?? "Session"}
          </span>
        )}
        {/* On a narrow pane the actions scroll sideways rather than being cut off. */}
        <div
          className="flex min-w-24 shrink items-center gap-0.5 overflow-x-auto [scrollbar-width:none]"
          role="toolbar"
          aria-label="Session"
        >
          {/* One action for where I am: driving → Watch (view only, still holding it) and Hand
              back; holding it but watching → Drive; not holding it → Take over. */}
          {lease && live && role === "control" && lease.can.watch ? (
            <ToolbarButton
              icon={EyeIcon}
              label="Watch"
              collapseLabelBelow={false}
              onClick={() => setWatchOnly(true)}
            />
          ) : null}
          {lease && live && driving && role === "watch" ? (
            <ToolbarButton
              icon={HandIcon}
              label="Drive"
              collapseLabelBelow={false}
              onClick={() => setWatchOnly(false)}
            />
          ) : null}
          {lease && live && !driving && lease.can.control && !otherDriver ? (
            <ToolbarButton
              icon={HandIcon}
              label="Take over"
              collapseLabelBelow={false}
              disabled={takeOver.isPending}
              onClick={() => takeOver.mutate(leaseId)}
            />
          ) : null}
          {lease && driving && !mine ? (
            <ToolbarButton
              icon={Undo2Icon}
              label={
                lease.holder.kind === "person"
                  ? "Hand back"
                  : `Hand back to ${workerName(lease.holder.name)}`
              }
              collapseLabelBelow={false}
              onClick={() => setHandBack(true)}
            />
          ) : null}
          {lease && myLogin ? (
            <ToolbarButton
              icon={CheckIcon}
              label="Done"
              collapseLabelBelow={false}
              disabled={end.isPending}
              onClick={() => end.mutate({ id: leaseId, login: true })}
            />
          ) : lease?.can.stop ? (
            <ToolbarButton
              icon={SquareIcon}
              label="End"
              collapseLabelBelow={false}
              className="hover:text-danger-text"
              disabled={end.isPending}
              onClick={() => end.mutate({ id: leaseId, login: false })}
            />
          ) : null}
          <ToolbarButton
            icon={MaximizeIcon}
            label="Full screen"
            collapseLabelBelow={1440}
            onClick={fullScreen}
          />
          {standalone ? null : (
            <ToolbarButton
              icon={ExternalLinkIcon}
              label="Open in new window"
              collapseLabelBelow={1440}
              onClick={() =>
                window.open(standaloneHref(slug, leaseId), `screen-${leaseId}`, "noopener")
              }
            />
          )}
          {lease && mine ? null : (
            <InfoTip label="About taking over">
              Watch is read only. Take over pauses the agent until you hand it back.
            </InfoTip>
          )}
        </div>
      </div>
      {/* A browser I drive gets an address bar (back, reload, go to); watchers see the screen. */}
      {lease && live && lease.kind === "browser" && role === "control" ? (
        <AddressBar key={leaseId} api={api} co={co} leaseId={leaseId} />
      ) : null}
      <LiveScreen api={api} co={co} leaseId={leaseId} role={role} starting={starting} />
      {handBack ? (
        <HandBackDialog
          api={api}
          co={co}
          leaseId={leaseId}
          agent={lease && lease.holder.kind !== "person" ? workerName(lease.holder.name) : null}
          onDone={() => {
            void refresh();
            // A late answer for a session no longer shown must not close another's dialog.
            if (shown.current === handBackFor) setHandBack(false);
          }}
          onCancel={() => setHandBack(false)}
        />
      ) : null}
    </div>
  );
}

/** One session alone, edge to edge (`/screens/<lease>?c=<slug>`). */
export function SessionWindow({
  api,
  co,
  slug,
  leaseId,
}: {
  api: Api;
  co: string;
  slug: string;
  leaseId: string;
}) {
  const { me } = useSession();
  const overview = useQuery(screensOverviewQuery(api, co));
  const lease = overview.data ? findLease(overview.data, leaseId) : null;
  const membership = me.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  return (
    <div className="flex h-dvh w-full overflow-hidden" data-slot="screen-window">
      <SessionPane
        api={api}
        co={co}
        slug={slug}
        leaseId={leaseId}
        lease={lease}
        me={membership}
        standalone
      />
    </div>
  );
}

/** The overview with one lease replaced (a take-over shows at once, before the refetch). */
function patchLease(o: unknown, l: Lease): unknown {
  const ov = o as { live?: Lease[]; needs_you?: Lease[] } | undefined;
  if (!ov?.live) return o;
  const swap = (xs: Lease[]) => xs.map((x) => (x.id === l.id ? l : x));
  return { ...ov, live: swap(ov.live), needs_you: swap(ov.needs_you ?? []) };
}
